import { ResponseBlockType, SupportBlock, SupportBlockType, SupportMoney } from './types';

export const RESPONSE_BLOCK_TYPES: readonly ResponseBlockType[] = [
  'support_ticket_form',
  'clarifying_question',
  'support_ticket_table',
  'support_ticket_record',
  'renewal_review_request',
  'weekly_update_report',
  'multi_answer_panel',
  'report_generated',
  'governed_analysis',
  'growth_planner_handoff'
];

const SUPPORT_BLOCK_TYPES: ReadonlySet<string> = new Set<SupportBlockType>([
  ...RESPONSE_BLOCK_TYPES,
  'feature_subscriptions',
  'support_ticket_draft',
  'out_of_scope',
  'multi_answer',
  'contract_summary',
  'weekly_update',
  'incidents_maintenance',
  'support_ticket_list',
  'support_ticket_detail',
  'renewal_review_form',
  'renewal_review_success',
  'text',
  'chart',
  'key_value',
  'table',
  'metric_tiles',
  'bullet_list',
  'banner',
  'action_row',
  'confidence',
  'panel'
]);

/** FR §4.8: current records, which take no date range unless the payload says otherwise. */
const CURRENT_RECORD_TYPES: ReadonlySet<string> = new Set<SupportBlockType>([
  'contract_summary',
  'support_ticket_list',
  'support_ticket_detail',
  'support_ticket_draft',
  'incidents_maintenance',
  'renewal_review_form',
  'renewal_review_success',
  'support_ticket_form',
  'support_ticket_table',
  'support_ticket_record',
  'renewal_review_request'
]);

export const blockPeriodApplies = (block: { type: string; period_applies?: boolean }): boolean =>
  block.period_applies ?? !CURRENT_RECORD_TYPES.has(block.type);

export const isKnownSupportBlock = (block: unknown): block is SupportBlock =>
  typeof block === 'object' &&
  block !== null &&
  SUPPORT_BLOCK_TYPES.has((block as { type?: unknown }).type as string);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ISO_PATTERN = /^(\d{4})-(\d{2})(?:-(\d{2}))?(?:T(\d{2}):(\d{2}))?/;

// Wall-clock reading of the ISO string itself, so the browser's own timezone never shifts a value
// the backend already expressed in the tenant's offset.
const parseIso = (value: string) => {
  const match = ISO_PATTERN.exec(value);
  if (match === null) {
    return null;
  }
  const [, year, month, day, hour, minute] = match;
  return { year, month: MONTHS[Number(month) - 1], day, hour, minute };
};

export const formatSupportDate = (value: string): string => {
  const parts = parseIso(value);
  return parts === null || parts.day === undefined
    ? value
    : `${parts.day} ${parts.month} ${parts.year}`;
};

export const formatSupportDateTime = (value: string): string => {
  const parts = parseIso(value);
  if (parts === null || parts.day === undefined || parts.hour === undefined) {
    return value;
  }
  return `${parts.day} ${parts.month} ${parts.year} ${parts.hour}:${parts.minute}`;
};

export const formatSupportYearMonth = (value: string): string => {
  const parts = parseIso(value);
  return parts === null ? value : `${parts.month} ${parts.year}`;
};

export const formatSupportMoney = ({ amount, currency }: SupportMoney): string =>
  `${currency} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
